import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import maplibregl from 'maplibre-gl';
import MapLibreCspWorker from 'maplibre-gl/dist/maplibre-gl-csp-worker?worker';
import mapLibreCspWorkerUrl from 'maplibre-gl/dist/maplibre-gl-csp-worker.js?url';
import 'maplibre-gl/dist/maplibre-gl.css';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGeoIntelData, GeoClusterPoint, GeoContratoPoint, Criticidade } from '@/hooks/useGeoIntelData';
import { useAuth } from '@/contexts/AuthContext';
import { triggerRuntimeRecovery } from '@/lib/runtimeRecovery';
import { RefreshCw, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

import MapToolbar from '@/components/map/MapToolbar';
import MapDetailPanel, { SelectedItem } from '@/components/map/MapDetailPanel';
import MapLegend from '@/components/map/MapLegend';
import { buildClustersGeoJSON, buildContratosGeoJSON, CRIT_COLORS, calculateBounds } from '@/components/map/mapHelpers';

// ── Componente ──

console.info('[GeoIntel:DIAG] MapPage MODULE LOADED', { timestamp: new Date().toISOString(), build: '__BUILD__' });

let mapLibreWorkerConfigured = false;
const ensureMapLibreWorker = () => {
  if (mapLibreWorkerConfigured) return;

  if (import.meta.env.PROD) {
    maplibregl.setWorkerUrl(mapLibreCspWorkerUrl);
    console.info('[GeoIntel:DIAG] MapLibre worker configured for PROD (CSP URL)', {
      workerUrl: mapLibreCspWorkerUrl,
    });
  } else {
    (maplibregl as any).workerClass = MapLibreCspWorker;
    console.info('[GeoIntel:DIAG] MapLibre worker configured for DEV (workerClass)');
  }

  mapLibreWorkerConfigured = true;
};

const MapPage: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [showClusters, setShowClusters] = useState(true);
  const [showContratos, setShowContratos] = useState(true);
  const [showGeometries, setShowGeometries] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [activeCriticidades, setActiveCriticidades] = useState<Set<Criticidade>>(
    new Set(['critico', 'alerta', 'ok', 'sem_dados'])
  );

  const { isAdmin } = useAuth();
  const { clusters, contratos, loading, refetch } = useGeoIntelData();

  // ── Criticidade counts ──
  const critCounts = useMemo(() => {
    const counts: Record<Criticidade, number> = { critico: 0, alerta: 0, ok: 0, sem_dados: 0 };
    clusters.forEach(c => counts[c.criticidade]++);
    contratos.forEach(c => counts[c.criticidade]++);
    return counts;
  }, [clusters, contratos]);

  // ── Init map ──
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      ensureMapLibreWorker();
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'carto-tiles': {
              type: 'raster',
              tiles: [
                'https://a.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png',
                'https://b.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png',
                'https://c.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png',
              ],
              tileSize: 256,
              attribution: '© CARTO © OSM',
            },
            'carto-labels': {
              type: 'raster',
              tiles: [
                'https://a.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}@2x.png',
                'https://b.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}@2x.png',
                'https://c.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}@2x.png',
              ],
              tileSize: 256,
            },
          },
          layers: [
            { id: 'carto-base', type: 'raster', source: 'carto-tiles', minzoom: 0, maxzoom: 20 },
            { id: 'carto-labels', type: 'raster', source: 'carto-labels', minzoom: 0, maxzoom: 20, paint: { 'raster-opacity': 0.6 } },
          ],
        },
        center: [-55, -14],
        zoom: 3.5,
        minZoom: 2,
        maxZoom: 18,
      });

      map.current.addControl(new maplibregl.NavigationControl(), 'bottom-right');
      map.current.addControl(new maplibregl.ScaleControl({ maxWidth: 120 }), 'bottom-right');

      map.current.on('load', () => {
        console.info('[GeoIntel:DIAG] map.on("load") FIRED', { timestamp: new Date().toISOString() });
        // Expose map globally for DevTools inspection
        (window as any).map = map.current;
        console.info('[GeoIntel:DIAG] window.map exposed — use window.map.getSource("clusters-points") in DevTools');
        setMapLoaded(true);
        setMapError(null);
        initSources();
        console.info('[GeoIntel:DIAG] initSources() complete — sources added to map');
        initLayers();
        console.info('[GeoIntel:DIAG] initLayers() complete — layers added to map');
        initInteractions();
        console.info('[GeoIntel:DIAG] initInteractions() complete');
        // Verify sources exist
        const m = map.current;
        if (m) {
          console.info('[GeoIntel:DIAG] Post-init source check', {
            'clusters-points': !!m.getSource('clusters-points'),
            'contratos-points': !!m.getSource('contratos-points'),
            'cluster-geometries': !!m.getSource('cluster-geometries'),
          });
        }
      });

      map.current.on('error', (e) => {
        const message = e.error?.message || 'Erro desconhecido';
        const m = map.current;
        const sourceId = String((e as any)?.sourceId ?? '');
        const tile = (e as any)?.tile;
        const hasCoreSources = !!m?.getSource('clusters-points') || !!m?.getSource('contratos-points');
        const mapSeemsOperational = !!m?.isStyleLoaded() && hasCoreSources;
        const isReferenceFailure = /\bis not defined\b/i.test(message);
        const isCriticalSourceError = ['clusters-points', 'contratos-points', 'cluster-geometries'].includes(sourceId);

        console.warn('[GeoIntel:DIAG] map.on("error") captured', {
          timestamp: new Date().toISOString(),
          message,
          sourceId,
          tile,
          mapSeemsOperational,
          isReferenceFailure,
          isCriticalSourceError,
        });

        if (import.meta.env.PROD && isReferenceFailure && (isCriticalSourceError || /cluster|contrato/i.test(message))) {
          console.error('[GeoIntel:DIAG] Critical MapLibre runtime error in PROD — triggering recovery', {
            message,
            sourceId,
            tile,
          });
          triggerRuntimeRecovery('map-runtime-error', { message, sourceId, tile, context: 'map.on(error)' });
          return;
        }

        // Em produção, alguns erros de tile/runtime são não-fatais: mantém mapa e dados renderizados.
        if (mapSeemsOperational) return;

        setMapError(`Erro ao carregar mapa: ${message}`);
      });

      return () => {
        popupRef.current?.remove();
        map.current?.remove();
        map.current = null;
      };
    } catch (error: any) {
      setMapError(`Erro ao inicializar mapa: ${error.message}`);
    }
  }, []);

  // ── Init sources ──
  const initSources = useCallback(() => {
    const m = map.current;
    if (!m) return;

    // Cluster geometry polygons source
    m.addSource('cluster-geometries', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Cluster points source
    m.addSource('clusters-points', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Contrato points source
    m.addSource('contratos-points', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }, []);

  // ── Init layers ──
  const initLayers = useCallback(() => {
    const m = map.current;
    if (!m) return;

    // Cluster geometry fill
    m.addLayer({
      id: 'cluster-geometry-fill',
      type: 'fill',
      source: 'cluster-geometries',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.08,
      },
    });

    // Cluster geometry border
    m.addLayer({
      id: 'cluster-geometry-line',
      type: 'line',
      source: 'cluster-geometries',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 0.45,
        'line-dasharray': [3, 2],
      },
    });

    // Contrato points — smaller, with soft glow effect via two layers
    m.addLayer({
      id: 'contratos-glow',
      type: 'circle',
      source: 'contratos-points',
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          3, 8,
          8, 12,
          14, 16,
        ],
        'circle-color': ['get', 'critColor'],
        'circle-opacity': 0.15,
        'circle-blur': 1,
      },
    });

    m.addLayer({
      id: 'contratos-layer',
      type: 'circle',
      source: 'contratos-points',
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          3, 4.5,
          8, 7,
          14, 10,
        ],
        'circle-color': ['get', 'critColor'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-opacity': 1,
      },
    });

    // Cluster glow
    m.addLayer({
      id: 'clusters-glow',
      type: 'circle',
      source: 'clusters-points',
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          3, 18,
          8, 28,
          14, 36,
        ],
        'circle-color': ['get', 'critColor'],
        'circle-opacity': 0.12,
        'circle-blur': 1,
      },
    });

    // Cluster points (larger circles with label)
    m.addLayer({
      id: 'clusters-layer',
      type: 'circle',
      source: 'clusters-points',
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          3, 11,
          8, 17,
          14, 24,
        ],
        'circle-color': ['get', 'critColor'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 3,
        'circle-opacity': 1,
      },
    });

    // Cluster labels
    m.addLayer({
      id: 'clusters-labels',
      type: 'symbol',
      source: 'clusters-points',
      layout: {
        'text-field': ['get', 'label'],
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          3, 10,
          8, 13,
          14, 17,
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.45)',
        'text-halo-width': 1.5,
      },
    });

    // Alert badge layer (small red circle on clusters with alerts)
    m.addLayer({
      id: 'clusters-alerts',
      type: 'circle',
      source: 'clusters-points',
      filter: ['>', ['get', 'total_alertas'], 0],
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          3, 6,
          8, 8,
          14, 10,
        ],
        'circle-color': CRIT_COLORS.critico,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-translate': [9, -9],
      },
    });

    // Alert count label
    m.addLayer({
      id: 'clusters-alerts-label',
      type: 'symbol',
      source: 'clusters-points',
      filter: ['>', ['get', 'total_alertas'], 0],
      layout: {
        'text-field': ['to-string', ['get', 'total_alertas']],
        'text-size': 9,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-offset': [0.65, -0.65],
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
  }, []);

  // ── Init interactions ──
  const initInteractions = useCallback(() => {
    const m = map.current;
    if (!m) return;

    // Hover cursor
    m.on('mouseenter', 'clusters-layer', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'clusters-layer', () => { m.getCanvas().style.cursor = ''; popupRef.current?.remove(); });
    m.on('mouseenter', 'contratos-layer', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'contratos-layer', () => { m.getCanvas().style.cursor = ''; popupRef.current?.remove(); });
    m.on('mouseenter', 'cluster-geometry-fill', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'cluster-geometry-fill', () => { m.getCanvas().style.cursor = ''; });

    // Hover tooltip for clusters
    m.on('mousemove', 'clusters-layer', (e) => {
      if (!e.features?.[0]) return;
      const props = e.features[0].properties;
      popupRef.current?.remove();
      popupRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 15, className: 'map-tooltip' })
        .setLngLat(e.lngLat)
        .setHTML(`<strong>${props?.name || ''}</strong><br/><span style="font-size:11px;color:#666">${props?.contratos_count || 0} contratos · ${props?.total_alertas || 0} alertas</span>`)
        .addTo(m);
    });

    // Hover tooltip for contratos
    m.on('mousemove', 'contratos-layer', (e) => {
      if (!e.features?.[0]) return;
      const props = e.features[0].properties;
      popupRef.current?.remove();
      const spi = props?.spi != null ? Number(props.spi).toFixed(3) : '—';
      popupRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10, className: 'map-tooltip' })
        .setLngLat(e.lngLat)
        .setHTML(`<strong>${props?.nome || ''}</strong><br/><span style="font-size:11px;color:#666">${props?.cluster_name || ''} · SPI ${spi}</span>`)
        .addTo(m);
    });

    // Click handlers
    m.on('click', 'clusters-layer', (e) => {
      if (!e.features?.[0]) return;
      const id = e.features[0].properties?.id;
      // We'll set selectedItem via React state - find from data
      setSelectedItem(prev => {
        // Will be resolved in the effect
        return { type: 'cluster', data: { __pendingId: id } as any };
      });
    });

    m.on('click', 'contratos-layer', (e) => {
      if (!e.features?.[0]) return;
      const id = e.features[0].properties?.id;
      setSelectedItem(prev => {
        return { type: 'contrato', data: { __pendingId: id } as any };
      });
    });
  }, []);

  // ── Resolve pending selection ──
  useEffect(() => {
    if (!selectedItem) return;
    const data = selectedItem.data as any;
    if (!data.__pendingId) return;

    if (selectedItem.type === 'cluster') {
      const found = clusters.find(c => c.id === data.__pendingId);
      if (found) setSelectedItem({ type: 'cluster', data: found });
      else setSelectedItem(null);
    } else {
      const found = contratos.find(c => c.id === data.__pendingId);
      if (found) setSelectedItem({ type: 'contrato', data: found });
      else setSelectedItem(null);
    }
  }, [selectedItem, clusters, contratos]);

  // ── Update data sources when data changes ──
  useEffect(() => {
    const m = map.current;
    
    console.info('[GeoIntel:DIAG] useEffect[data] triggered', {
      timestamp: new Date().toISOString(),
      mapExists: !!m,
      mapLoaded,
      loading,
      clustersCount: clusters.length,
      contratosCount: contratos.length,
      clustersWithValidCoords: clusters.filter(c => c.has_valid_coordinates).length,
      contratosWithValidCoords: contratos.filter(c => c.has_valid_coordinates).length,
    });
    
    if (!m || !mapLoaded || loading) {
      console.warn('[GeoIntel:DIAG] useEffect[data] SKIPPED — guard condition met', {
        mapNull: !m,
        mapLoaded,
        loading,
      });
      return;
    }

    // Build filter: only show active criticidades
    const activeCritSet = activeCriticidades;

    const filteredClusters = clusters.filter(c =>
      activeCritSet.has(c.criticidade) &&
      (searchQuery === '' || c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    const filteredContratos = contratos.filter(c =>
      activeCritSet.has(c.criticidade) &&
      (searchQuery === '' || c.nome.toLowerCase().includes(searchQuery.toLowerCase()) || c.cluster_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Update sources
    const clustersSrc = m.getSource('clusters-points') as maplibregl.GeoJSONSource;
    const contratosSrc = m.getSource('contratos-points') as maplibregl.GeoJSONSource;

    const clustersGeoJSON = buildClustersGeoJSON(filteredClusters);
    const contratosGeoJSON = buildContratosGeoJSON(filteredContratos);

    console.info('[GeoIntel:DIAG] Sources check before setData', {
      clustersSrcExists: !!clustersSrc,
      contratosSrcExists: !!contratosSrc,
      clustersFeatures: clustersGeoJSON.features.length,
      contratosFeatures: contratosGeoJSON.features.length,
      filteredClusters: filteredClusters.length,
      filteredContratos: filteredContratos.length,
    });

    if (clustersSrc) clustersSrc.setData(clustersGeoJSON);
    if (contratosSrc) contratosSrc.setData(contratosGeoJSON);

    console.info('[GeoIntel:DIAG] setData() applied successfully — markers should be visible');
    console.info('[GeoIntel:DIAG] source feature counts after setData', {
      clustersSourceFeatures: clustersGeoJSON.features.length,
      contratosSourceFeatures: contratosGeoJSON.features.length,
      clusterGeometriesSourceFeatures: 0,
      hasClustersSource: !!m.getSource('clusters-points'),
      hasContratosSource: !!m.getSource('contratos-points'),
      hasGeometriesSource: !!m.getSource('cluster-geometries'),
      runtimeClustersFeatures: ((m.getSource('clusters-points') as any)?._data?.features?.length ?? null),
      runtimeContratosFeatures: ((m.getSource('contratos-points') as any)?._data?.features?.length ?? null),
      runtimeGeometriesFeatures: ((m.getSource('cluster-geometries') as any)?._data?.features?.length ?? null),
    });
    // Update cluster geometries
    const geoSrc = m.getSource('cluster-geometries') as maplibregl.GeoJSONSource;
    if (geoSrc) {
      geoSrc.setData({ type: 'FeatureCollection', features: [] });
    }

    // Auto-fit bounds after data update
    const allPoints = [
      ...filteredClusters.filter(c => c.has_valid_coordinates),
      ...filteredContratos.filter(c => c.has_valid_coordinates),
    ];
    if (allPoints.length > 0) {
      const bounds = calculateBounds(filteredClusters, filteredContratos);
      if (bounds) {
        m.fitBounds(bounds as [number, number, number, number], {
          padding: 80,
          maxZoom: 10,
          duration: 1200,
        });
        hasInitialFit.current = true;
        console.info('[GeoIntel:DIAG] fitBounds applied — centering on', allPoints.length, 'valid points');
      }
    }
  }, [clusters, contratos, mapLoaded, loading, activeCriticidades, searchQuery]);

  // ── Layer visibility ──
  useEffect(() => {
    const m = map.current;
    if (!m || !mapLoaded) return;
    const vis = (show: boolean) => show ? 'visible' : 'none';

    try {
      m.setLayoutProperty('clusters-layer', 'visibility', vis(showClusters));
      m.setLayoutProperty('clusters-glow', 'visibility', vis(showClusters));
      m.setLayoutProperty('clusters-labels', 'visibility', vis(showClusters));
      m.setLayoutProperty('clusters-alerts', 'visibility', vis(showClusters));
      m.setLayoutProperty('clusters-alerts-label', 'visibility', vis(showClusters));
      m.setLayoutProperty('contratos-layer', 'visibility', vis(showContratos));
      m.setLayoutProperty('contratos-glow', 'visibility', vis(showContratos));
      m.setLayoutProperty('cluster-geometry-fill', 'visibility', vis(showGeometries));
      m.setLayoutProperty('cluster-geometry-line', 'visibility', vis(showGeometries));
    } catch {
      // layers not yet added
    }
  }, [showClusters, showContratos, showGeometries, mapLoaded]);

  // ── Fit bounds on first data load ──
  const hasInitialFit = useRef(false);
  useEffect(() => {
    if (!map.current || !mapLoaded || loading || hasInitialFit.current) return;
    if (clusters.length === 0 && contratos.length === 0) return;

    const bounds = calculateBounds(clusters, contratos);
    if (bounds) {
      map.current.fitBounds(bounds as [number, number, number, number], {
        padding: 80,
        maxZoom: 10,
        duration: 1200,
      });
      hasInitialFit.current = true;
    }
  }, [clusters, contratos, mapLoaded, loading]);

  // ── Zoom to extent ──
  const zoomToExtent = useCallback(() => {
    const bounds = calculateBounds(clusters, contratos);
    if (bounds && map.current) {
      map.current.fitBounds(bounds as [number, number, number, number], {
        padding: 80,
        maxZoom: 10,
        duration: 1200,
      });
    }
  }, [clusters, contratos]);

  // ── Toggle criticidade ──
  const toggleCriticidade = useCallback((c: Criticidade) => {
    setActiveCriticidades(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }, []);

  // ── Render ──
  return (
    <div>
      <div className="relative h-[calc(100vh-8rem)]">
        <div className="absolute inset-0 rounded-xl overflow-hidden border border-border/60 shadow-lg">
          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 z-20 bg-background/80 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center space-y-4">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Carregando dados geoespaciais...</p>
              </div>
            </div>
          )}

          {/* Error overlay */}
          {mapError && (
            <div className="absolute inset-0 z-30 bg-background/95 flex items-center justify-center p-8">
              <Card className="max-w-md">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 text-destructive">
                    <AlertTriangle className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Erro no Mapa</p>
                      <p className="text-sm text-muted-foreground">{mapError}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Map container */}
          <div ref={mapContainer} className="w-full h-full" />

          {/* Toolbar */}
          <MapToolbar
            showClusters={showClusters}
            showContratos={showContratos}
            showGeometries={showGeometries}
            searchQuery={searchQuery}
            clustersCount={clusters.length}
            contratosCount={contratos.length}
            isAdmin={isAdmin}
            showDiagnostic={showDiagnostic}
            onToggleClusters={() => setShowClusters(v => !v)}
            onToggleContratos={() => setShowContratos(v => !v)}
            onToggleGeometries={() => setShowGeometries(v => !v)}
            onSearchChange={setSearchQuery}
            onZoomToExtent={zoomToExtent}
            onToggleDiagnostic={() => setShowDiagnostic(v => !v)}
            onRefresh={() => refetch()}
          />

          {/* Legend */}
          <MapLegend
            activeCriticidades={activeCriticidades}
            onToggleCriticidade={toggleCriticidade}
            counts={critCounts}
          />

          {/* Status badge */}
          {mapLoaded && !mapError && (
            <div className="absolute bottom-3 right-14 z-10">
              <Badge variant="outline" className="bg-card/85 backdrop-blur-sm text-xs h-6 border-border/50">
                <CheckCircle2 className="w-2.5 h-2.5 mr-1 text-success" />
                CARTO
              </Badge>
            </div>
          )}

          {/* Diagnostic panel (admin) */}
          {isAdmin && showDiagnostic && (
            <div className="absolute bottom-12 left-3 z-10 w-64">
              <Card className="bg-card/95 backdrop-blur-xl shadow-lg border-border/60">
                <CardHeader className="py-2 px-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-primary" />
                      Diagnóstico
                    </CardTitle>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setShowDiagnostic(false)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="py-2 px-3 space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="text-center p-1.5 bg-muted/30 rounded-lg">
                      <p className="font-semibold">{clusters.length}</p>
                      <p className="text-xs text-muted-foreground">Clusters</p>
                    </div>
                    <div className="text-center p-1.5 bg-muted/30 rounded-lg">
                      <p className="font-semibold">{contratos.length}</p>
                      <p className="text-xs text-muted-foreground">Contratos</p>
                    </div>
                  </div>

                  {clusters.filter(c => !c.has_valid_coordinates).length > 0 && (
                    <div className="flex items-center gap-1.5 text-warning text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      <span>{clusters.filter(c => !c.has_valid_coordinates).length} cluster(s) sem coord. válida</span>
                    </div>
                  )}

                  {clusters.filter(c => c.criticidade === 'critico').length > 0 && (
                    <div className="flex items-center gap-1.5 text-destructive text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      <span>{clusters.filter(c => c.criticidade === 'critico').length} cluster(s) crítico(s)</span>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground/60 pt-1 border-t border-border/30">
                    Renderização: Layers nativas GeoJSON · Sem DOM markers
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Detail panel */}
          {selectedItem && !(selectedItem.data as any).__pendingId && (
            <MapDetailPanel item={selectedItem} onClose={() => setSelectedItem(null)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MapPage;
